/**
 * Get merged user data from Cognito and AppSync
 * Cognito stores ONLY email and email_verified
 * All other user data (name, nickname, birthdate, picture, etc.) comes from AppSync
 */

import { CognitoIdentityProviderClient, GetUserCommand } from "@aws-sdk/client-cognito-identity-provider";
import { getTokensFromCache, getUserDataFromCache, saveUserDataToCache } from './tokenCache';
import { getUserProfile } from '../../user/api';

const AWS_REGION = process.env.REACT_APP_AWS_REGION || 'us-west-2';
const cognitoClient = new CognitoIdentityProviderClient({ 
  region: AWS_REGION 
});

/**
 * Cognito user data - only email and verification status
 * All other user profile data is stored in AppSync
 */
export interface CognitoUserData {
  sub: string; // User ID
  email: string;
  email_verified: boolean;
}

export interface MergedUserData {
  // From Cognito
  sub: string; // User ID
  email: string;
  email_verified: boolean;
  // From AppSync
  id?: string;
  birthdate?: string;
  picture_url?: string;
  nickname?: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  lastSeenAt?: string;
  createdAt?: string;
}

/**
 * Get user data from Cognito using AccessToken
 * Only retrieves email and email_verified (all other data comes from AppSync)
 * Exported for use in other modules
 */
export const getCognitoUserData = async (accessToken: string): Promise<CognitoUserData | null> => {
  try {
    // Remove Bearer prefix if present
    const token = accessToken.startsWith('Bearer ') ? accessToken.substring(7) : accessToken;
    
    const command = new GetUserCommand({
      AccessToken: token
    });
    
    const response = await cognitoClient.send(command);
    
    if (response.UserAttributes) {
      const attributes = response.UserAttributes;
      
      // Extract attributes
      const emailAttr = attributes.find(attr => attr.Name === 'email');
      const emailVerifiedAttr = attributes.find(attr => attr.Name === 'email_verified');
      
      const cognitoData: CognitoUserData = {
        sub: response.UserAttributes?.find(attr => attr.Name === 'sub')?.Value || '',
        email: emailAttr?.Value || '',
        email_verified: emailVerifiedAttr?.Value === 'true',
      };
      
      console.log('✅ Cognito user data retrieved:', cognitoData);
      return cognitoData;
    }
    
    return null;
  } catch (error: any) {
    console.error('❌ Failed to get Cognito user data:', error);
    return null;
  }
};

/**
 * Get the user's profile from the REST user API (D-2; AppSync retired).
 * Returns null when the profile isn't created yet (404) or on error.
 */
const getProfileData = async (userId: string, accessToken: string): Promise<any | null> => {
  try {
    const profile = await getUserProfile(userId, accessToken);
    if (!profile) {
      console.log('⚠️ User profile not found yet (may not be created)');
      return null;
    }
    return profile;
  } catch (error: any) {
    console.error('❌ Failed to get user profile:', error);
    return null;
  }
};

/**
 * Get merged user data from Cognito (email) + the REST user profile.
 * Only calls APIs if tokens are present and not expired.
 */
export const getMergedUserData = async (): Promise<MergedUserData | null> => {
  try {
    // Check if tokens are present and not expired
    const tokens = getTokensFromCache();
    if (!tokens) {
      console.log('⚠️ No valid tokens available, skipping user data fetch');
      return null;
    }
    
    // Extract tokens without Bearer prefix for Cognito API
    const accessToken = tokens.accessToken;
    const idToken = tokens.idToken;
    
    // Try to get user data from cache first
    let cognitoData = getUserDataFromCache();
    
    // If not in cache or expired, fetch from Cognito API
    if (!cognitoData) {
      console.log('📥 Fetching Cognito user data from API...');
      cognitoData = await getCognitoUserData(accessToken);
      
      // Save to cache if successfully fetched
      if (cognitoData) {
        saveUserDataToCache(cognitoData, tokens.expiresAt);
      }
    } else {
      console.log('✅ Using cached Cognito user data');
    }
    if (!cognitoData) {
      console.error('❌ Failed to get Cognito user data');
      return null;
    }
    
    // Fetch the profile from the REST user API (keyed by the Cognito sub).
    const profileData = await getProfileData(cognitoData.sub, accessToken);

    // Merge responses
    const mergedData: MergedUserData = {
      // From Cognito
      sub: cognitoData.sub,
      email: cognitoData.email,
      email_verified: cognitoData.email_verified,
      // From the REST profile (if available) - profile data takes precedence.
      // Server fields are camelCase; map to the MergedUserData shape.
      ...(profileData ? {
        id: profileData.id,
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        nickname: profileData.nickname,
        birthdate: profileData.birthDate,
        picture_url: profileData.pictureUrl,
        lastSeenAt: profileData.lastSeenAt,
        createdAt: profileData.createdAt,
      } : {})
    };
    
    console.log('✅ Merged user data:', mergedData);
    return mergedData;
  } catch (error: any) {
    console.error('❌ Failed to get merged user data:', error);
    return null;
  }
};

